import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  InputLabel,
  Grid,
  CircularProgress,
  Divider,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import { decryptText, decryptAppId } from '../../../utils/encryption';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import FlightIcon from '@mui/icons-material/Flight';
import HotelIcon from '@mui/icons-material/Hotel';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import CancelIcon from '@mui/icons-material/Cancel';

const AssessorLogisticDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ─── Decrypt URL Params ─────────────────────────────────────
  const getOrgIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedOrgId = searchParams.get('orgId');
    if (encryptedOrgId) {
      try {
        return decryptText(encryptedOrgId);
      } catch {
        return encryptedOrgId;
      }
    }
    return null;
  };

  const getAppIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAppId = searchParams.get('appId');
    if (encryptedAppId) {
      try {
        return decryptAppId(encryptedAppId);
      } catch {
        return encryptedAppId;
      }
    }
    return null;
  };

  const getAssessorIdFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessorId = searchParams.get('assessorId');
    if (encryptedAssessorId) {
      try {
        return decryptText(encryptedAssessorId);
      } catch {
        return encryptedAssessorId;
      }
    }
    return null;
  };

  const getAssessmentDetailFromQuery = () => {
    const searchParams = new URLSearchParams(location.search);
    const encryptedAssessmentDetail = searchParams.get('assessmentDetail');
    if (encryptedAssessmentDetail) {
      try {
        return JSON.parse(decryptText(encryptedAssessmentDetail));
      } catch {
        return JSON.parse(encryptedAssessmentDetail);
      }
    }
    return null;
  };

  const [orgId] = useState(getOrgIdFromQuery());
  const [appId] = useState(getAppIdFromQuery());
  const [assessorId] = useState(getAssessorIdFromQuery());
  const [assessmentDetail] = useState(getAssessmentDetailFromQuery());

  // ─── Lab Details (Read‑only) ───────────────────────────────
  const [labDetails] = useState({
    labId: assessmentDetail?.ApplicationNo || '',
    userName: assessmentDetail?.LoginName || '',
    laboratoryName: assessmentDetail?.LaboratoryName || '',
    assessmentType: assessmentDetail?.AssessmentType || '',
    assessmentDate: assessmentDetail?.AssessmentDate || '',
    address: assessmentDetail?.Address || '',
    assessmentId: assessmentDetail?.assessmentId || '',
  });

  // ─── Form State ─────────────────────────────────────────────
  const [formData, setFormData] = useState({
    id: null,
    travelType: '',
    originCountry: '',
    originState: '',
    originCity: '',
    onwardTravelDate: null,
    onwardTravelType: '',
    onwardFlightTrainNo: '',
    seatPreference: '',
    destinationCountry: '',
    destinationState: '',
    destination: '',
    returnTravelDate: null,
    returnTravelType: '',
    returnFlightTrainNo: '',
    returnSeat: '',
    mealPreference: '',
    accommodationRequired: 'No',
    remarks: '',
  });

  // ─── Dropdown Data ───────────────────────────────────────────
  const [countries, setCountries] = useState([]);
  const [originStates, setOriginStates] = useState([]);
  const [originCities, setOriginCities] = useState([]);
  const [destStates, setDestStates] = useState([]);
  const [destCities, setDestCities] = useState([]);

  // ─── Seat Options ────────────────────────────────────────────
  const [seatOptions, setSeatOptions] = useState([]);
  const [returnSeatOptions, setReturnSeatOptions] = useState([]);

  // ─── UI State ────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showSendDO, setShowSendDO] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [buttonText, setButtonText] = useState('Save');
  const [errors, setErrors] = useState({});
  
  // ─── Password Dialog State ──────────────────────────────────
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordVerifying, setPasswordVerifying] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // ─── Load Data ──────────────────────────────────────────────
  useEffect(() => {
    if (appId && orgId) {
      loadData();
      loadCountries();
    }
  }, [appId, orgId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/get-details/${appId}/${orgId}`
      );

      if (response.data.success) {
        const data = response.data.data;
        if (data && data.id) {
          setFormData({
            id: data.id || null,
            travelType: data.travelType || '',
            originCountry: data.originCountry || '',
            originState: data.originState || '',
            originCity: data.originCity || '',
            onwardTravelDate: data.onwardTravelDate ? dayjs(data.onwardTravelDate) : null,
            onwardTravelType: data.onwardTravelType || '',
            onwardFlightTrainNo: data.onwardFlightTrainNo || '',
            seatPreference: data.seatPreference || '',
            destinationCountry: data.destinationCountry || '',
            destinationState: data.destinationState || '',
            destination: data.destination || '',
            returnTravelDate: data.returnTravelDate ? dayjs(data.returnTravelDate) : null,
            returnTravelType: data.returnTravelType || '',
            returnFlightTrainNo: data.returnFlightTrainNo || '',
            returnSeat: data.returnSeat || '',
            mealPreference: data.mealPreference || '',
            accommodationRequired: data.accommodationRequired === true || data.accommodationRequired === 1 ? 'Yes' : 'No',
            remarks: data.remarks || '',
          });

          if (data.travelType === 'Outstation Travel') {
            if (data.originCountry) {
              await loadStates(data.originCountry, 'origin');
              if (data.originState) await loadCities(data.originState, 'origin');
            }
            if (data.destinationCountry) {
              await loadStates(data.destinationCountry, 'destination');
              if (data.destinationState) await loadCities(data.destinationState, 'destination');
            }
            if (data.onwardTravelType) updateSeatOptions(data.onwardTravelType, 'onward');
            if (data.returnTravelType) updateSeatOptions(data.returnTravelType, 'return');
          }
        }
        setButtonText('Edit');
        setIsEditMode(false);
      }

      await checkDOStatus();
      await checkSaveStatus();
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load details');
    } finally {
      setLoading(false);
    }
  };

  const loadCountries = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/assessor-logistic/countries`);
      if (response.data.success) setCountries(response.data.data || []);
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const loadStates = async (countryId, type) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/assessor-logistic/states/${countryId}`);
      if (response.data.success) {
        if (type === 'origin') setOriginStates(response.data.data || []);
        else setDestStates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading states:', error);
    }
  };

  const loadCities = async (stateId, type) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/assessor-logistic/cities/${stateId}`);
      if (response.data.success) {
        if (type === 'origin') setOriginCities(response.data.data || []);
        else setDestCities(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading cities:', error);
    }
  };

  const checkDOStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/details/check-do-status/${appId}/${orgId}`);
      if (response.data.success) setShowSendDO(response.data.showSendButton);
    } catch (error) {
      console.error('Error checking DO status:', error);
    }
  };

  const checkSaveStatus = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/assessor-logistic/details/check-save-status/${appId}/${orgId}`);
      if (response.data.success) {
        setCanSave(response.data.canSave);
        setButtonText(response.data.buttonText);
      }
    } catch (error) {
      console.error('Error checking save status:', error);
    }
  };

  // ─── Handlers ──────────────────────────────────────────────
  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleTravelTypeChange = (value) => {
    handleChange('travelType', value);
    if (value === 'Local Travel') {
      setFormData((prev) => ({
        ...prev,
        travelType: value,
        originCountry: '',
        originState: '',
        originCity: '',
        onwardTravelDate: null,
        onwardTravelType: '',
        onwardFlightTrainNo: '',
        seatPreference: '',
        destinationCountry: '',
        destinationState: '',
        destination: '',
        returnTravelDate: null,
        returnTravelType: '',
        returnFlightTrainNo: '',
        returnSeat: '',
        mealPreference: '',
      }));
      setSeatOptions([]);
      setReturnSeatOptions([]);
    }
  };

  const handleCountryChange = (countryId, type) => {
    if (type === 'origin') {
      handleChange('originCountry', countryId);
      handleChange('originState', '');
      handleChange('originCity', '');
      setOriginStates([]);
      setOriginCities([]);
      if (countryId) loadStates(countryId, 'origin');
    } else {
      handleChange('destinationCountry', countryId);
      handleChange('destinationState', '');
      handleChange('destination', '');
      setDestStates([]);
      setDestCities([]);
      if (countryId) loadStates(countryId, 'destination');
    }
  };

  const handleStateChange = (stateId, type) => {
    if (type === 'origin') {
      handleChange('originState', stateId);
      handleChange('originCity', '');
      setOriginCities([]);
      if (stateId) loadCities(stateId, 'origin');
    } else {
      handleChange('destinationState', stateId);
      handleChange('destination', '');
      setDestCities([]);
      if (stateId) loadCities(stateId, 'destination');
    }
  };

  const updateSeatOptions = (travelType, direction) => {
    let options = [];
    if (travelType === 'Flight') {
      options = [
        { value: 'Window', label: 'Window' },
        { value: 'Middle', label: 'Middle' },
        { value: 'Aisle', label: 'Aisle' },
      ];
    } else if (travelType === 'Train') {
      options = [
        { value: 'Lower', label: 'Lower' },
        { value: 'Middle', label: 'Middle' },
        { value: 'Upper', label: 'Upper' },
        { value: 'Side Lower', label: 'Side Lower' },
        { value: 'Side Upper', label: 'Side Upper' },
        { value: 'CC', label: 'CC' },
      ];
    }
    if (direction === 'onward') setSeatOptions(options);
    else setReturnSeatOptions(options);
  };

  const handleTravelTypeSelect = (value, direction) => {
    if (direction === 'onward') {
      handleChange('onwardTravelType', value);
      handleChange('seatPreference', '');
      updateSeatOptions(value, 'onward');
    } else {
      handleChange('returnTravelType', value);
      handleChange('returnSeat', '');
      updateSeatOptions(value, 'return');
    }
  };

  // ─── Validation ─────────────────────────────────────────────
  const validate = () => {
    const newErrors = {};
    if (!formData.travelType) newErrors.travelType = 'Travel type is required';
    if (formData.travelType === 'Outstation Travel') {
      if (!formData.originCountry) newErrors.originCountry = 'Origin Country is required';
      if (!formData.originState) newErrors.originState = 'Origin State is required';
      if (!formData.originCity) newErrors.originCity = 'Origin City is required';
      if (!formData.onwardTravelDate) newErrors.onwardTravelDate = 'Onward travel date is required';
      if (!formData.onwardTravelType) newErrors.onwardTravelType = 'Onward travel type is required';
      if (!formData.onwardFlightTrainNo) newErrors.onwardFlightTrainNo = 'Flight/Train/Bus No is required';
      if (!formData.destinationCountry) newErrors.destinationCountry = 'Destination Country is required';
      if (!formData.destinationState) newErrors.destinationState = 'Destination State is required';
      if (!formData.destination) newErrors.destination = 'Destination City is required';
      if (!formData.returnTravelDate) newErrors.returnTravelDate = 'Return travel date is required';
      if (!formData.returnTravelType) newErrors.returnTravelType = 'Return travel type is required';
      if (!formData.returnFlightTrainNo) newErrors.returnFlightTrainNo = 'Return Flight/Train/Bus No is required';
    }
    if (!formData.remarks?.trim()) newErrors.remarks = 'Remark is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Save / Update ──────────────────────────────────────────
  const handleSave = async () => {
    if (buttonText === 'Edit' && !isEditMode) {
      setIsEditMode(true);
      setButtonText('Update');
      return;
    }

    if (!validate()) {
      toast.error('Please fill all required fields');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        id: formData.id,
        appId,
        orgId,
        labId: labDetails.labId,
        userName: labDetails.userName,
        laboratoryName: labDetails.laboratoryName,
        address: labDetails.address,
        assessmentType: labDetails.assessmentType,
        assessmentDate: labDetails.assessmentDate,
        travelType: formData.travelType,
        originCountry: formData.originCountry,
        originState: formData.originState,
        originCity: formData.originCity,
        onwardTravelDate: formData.onwardTravelDate ? formData.onwardTravelDate.format('YYYY-MM-DD') : null,
        onwardTravelType: formData.onwardTravelType,
        onwardFlightTrainNo: formData.onwardFlightTrainNo,
        seatPreference: formData.seatPreference,
        destinationCountry: formData.destinationCountry,
        destinationState: formData.destinationState,
        destination: formData.destination,
        returnTravelDate: formData.returnTravelDate ? formData.returnTravelDate.format('YYYY-MM-DD') : null,
        returnTravelType: formData.returnTravelType,
        returnFlightTrainNo: formData.returnFlightTrainNo,
        returnSeat: formData.returnSeat,
        mealPreference: formData.mealPreference,
        accommodationRequired: formData.accommodationRequired === 'Yes' ? 1 : 0,
        remarks: formData.remarks,
        insertedBy: localStorage.getItem('orgId') || null,
        assessorId: assessorId || null,
        assessmentId: assessmentDetail?.assessmentId || null,
      };

      const response = await axios.post(`${process.env.REACT_APP_API_URL}/assessor-logistic/save-details`, payload);

      if (response.data.success) {
        toast.success(response.data.message);
        setIsEditMode(false);
        setButtonText('Edit');
        await loadData();
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error(error.response?.data?.message || 'Failed to save logistic details');
    } finally {
      setSaving(false);
    }
  };

  // ─── Cancel Edit ────────────────────────────────────────────
  const handleCancel = () => {
    setIsEditMode(false);
    setButtonText('Edit');
    loadData();
    toast.info('Edit cancelled');
  };

  // ─── Send to DO with Password Dialog ──────────────────────
  const handleSendToDO = () => {
    setPasswordDialogOpen(true);
    setPasswordInput('');
    setPasswordError('');
  };

  const handlePasswordConfirm = async () => {
    if (!passwordInput.trim()) {
      setPasswordError('Password is required');
      return;
    }

    setPasswordVerifying(true);
    setPasswordError('');
    try {
      // Verify password
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: localStorage.getItem('orgId'),
          password: passwordInput,
        }
      );

      if (!passwordResponse.data.success) {
        setPasswordError('Invalid password');
        setPasswordVerifying(false);
        return;
      }

      // Close dialog
      setPasswordDialogOpen(false);
      setPasswordVerifying(false);

      // Proceed with send to DO
      const response = await axios.put(`${process.env.REACT_APP_API_URL}/assessor-logistic/details/send-to-do`, {
        appId,
        orgId,
      });

      if (response.data.success) {
        toast.success('Application has been sent to DO successfully');
        setShowSendDO(false);
        setCanSave(false);
        setButtonText('Edit');
        await loadData();
      }
    } catch (error) {
      console.error('Error sending to DO:', error);
      toast.error('Failed to send to DO');
      setPasswordDialogOpen(false);
    } finally {
      setPasswordVerifying(false);
    }
  };

  const handlePasswordCancel = () => {
    setPasswordDialogOpen(false);
    setPasswordInput('');
    setPasswordError('');
    setPasswordVerifying(false);
  };

  // ─── Helper: Parse Assessment Date ─────────────────────────
  const parseAssessmentDate = (assessmentDateStr) => {
    if (!assessmentDateStr) return { start: null, end: null };
    const dateRegex = /Date from\s*:\s*(\d{2}\/\d{2}\/\d{4})\s*Date To\s*:\s*(\d{2}\/\d{2}\/\d{4})/i;
    const match = assessmentDateStr.match(dateRegex);
    if (match) {
      return { start: match[1], end: match[2] };
    }
    return { start: null, end: null };
  };

  // ─── Render ─────────────────────────────────────────────────
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const isReadOnly = !isEditMode && buttonText === 'Edit';

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Paper
          elevation={4}
          sx={{
            p: { xs: 1, sm: 2, md: 3 },
            borderRadius: 3,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8faff 100%)',
            border: '1px solid #e8edf5',
          }}
        >
          {/* ─── Header with Gradient ──────────────────────────── */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 2,
              p: 3,
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2,
            }}
          >
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" fontWeight="700" color="white">
                Assessor Logistic Details
              </Typography>
            </Box>
            {formData.id && (
              <Chip
                label="Saved"
                color="success"
                size="medium"
                icon={<SaveIcon />}
                sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 600 }}
              />
            )}
          </Box>

          {/* ─── Lab Details ───────────────────────────────────── */}
          <Box
            sx={{
              bgcolor: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
              p: 3,
              borderRadius: 2,
              mb: 4,
              borderLeft: '4px solid #1976d2',
            }}
          >
            <Typography variant="subtitle1" fontWeight="600" color="#1565c0" gutterBottom>
              <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#1976d2' }} />
              Laboratory Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Lab ID"
                  value={labDetails.labId}
                  InputProps={{ readOnly: true }}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="User Name"
                  value={labDetails.userName}
                  InputProps={{ readOnly: true }}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Laboratory Name"
                  value={labDetails.laboratoryName}
                  InputProps={{ readOnly: true }}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Assessment Type"
                  value={labDetails.assessmentType}
                  InputProps={{ readOnly: true }}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Assessment Date"
                  value={(() => {
                    const dates = parseAssessmentDate(assessmentDetail?.AssessmentDate);
                    return `${dates.start || ''} To ${dates.end || ''}`;
                  })()}
                  InputProps={{ readOnly: true }}
                  size="small"
                  variant="outlined"
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={labDetails.address}
                  InputProps={{ readOnly: true }}
                  size="small"
                  variant="outlined"
                  multiline
                  rows={2}
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* ─── Travel Type ───────────────────────────────────── */}
          <Box mb={4}>
            <FormControl component="fieldset" error={!!errors.travelType}>
              <FormLabel
                component="legend"
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  '& .MuiFormLabel-asterisk': { color: '#d32f2f' },
                }}
              >
                Travel Type *
              </FormLabel>
              <RadioGroup
                row
                value={formData.travelType}
                onChange={(e) => handleTravelTypeChange(e.target.value)}
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="Local Travel"
                  control={<Radio sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                  label="Local Travel"
                  disabled={isReadOnly}
                />
                <FormControlLabel
                  value="Outstation Travel"
                  control={<Radio sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />}
                  label="Outstation Travel"
                  disabled={isReadOnly}
                />
              </RadioGroup>
              {errors.travelType && (
                <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                  {errors.travelType}
                </Typography>
              )}
            </FormControl>
          </Box>

          {/* ─── Outstation Travel ────────────────────────────── */}
          {formData.travelType === 'Outstation Travel' && (
            <>
              <Divider sx={{ my: 3, borderColor: '#b0bec5' }} />
              <Box
                sx={{
                  bgcolor: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                  p: 3,
                  borderRadius: 2,
                  mb: 4,
                  borderLeft: '4px solid #388e3c',
                }}
              >
                <Typography variant="subtitle1" fontWeight="600" color="#1b5e20" gutterBottom>
                  Travel & Accommodation Details Onward 
                </Typography>

                {/* Origin */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!errors.originCountry}>
                      <InputLabel>Origin Country *</InputLabel>
                      <Select
                        value={formData.originCountry}
                        onChange={(e) => handleCountryChange(e.target.value, 'origin')}
                        disabled={isReadOnly}
                        label="Origin Country *"
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                      >
                        {countries.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.originCountry && (
                        <Typography variant="caption" color="error">
                          {errors.originCountry}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!errors.originState}>
                      <InputLabel>Origin State *</InputLabel>
                      <Select
                        value={formData.originState}
                        onChange={(e) => handleStateChange(e.target.value, 'origin')}
                        disabled={isReadOnly || !formData.originCountry}
                        label="Origin State *"
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                      >
                        {originStates.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.originState && (
                        <Typography variant="caption" color="error">
                          {errors.originState}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!errors.originCity}>
                      <InputLabel>Origin City *</InputLabel>
                      <Select
                        value={formData.originCity}
                        onChange={(e) => handleChange('originCity', e.target.value)}
                        disabled={isReadOnly || !formData.originState}
                        label="Origin City *"
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                      >
                        {originCities.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.originCity && (
                        <Typography variant="caption" color="error">
                          {errors.originCity}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Onward Travel */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Onward Travel Date *"
                        value={formData.onwardTravelDate}
                        onChange={(date) => handleChange('onwardTravelDate', date)}
                        disabled={isReadOnly}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!errors.onwardTravelDate,
                            helperText: errors.onwardTravelDate,
                            sx: { bgcolor: 'white', borderRadius: 1 },
                          },
                        }}
                        format="DD/MM/YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" error={!!errors.onwardTravelType}>
                      <FormLabel component="legend">Onward Travel Type *</FormLabel>
                      <RadioGroup
                        row
                        value={formData.onwardTravelType}
                        onChange={(e) => handleTravelTypeSelect(e.target.value, 'onward')}
                      >
                        <FormControlLabel
                          value="Flight"
                          control={<Radio sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />}
                          label="Flight"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="Train"
                          control={<Radio sx={{ color: '#d84315', '&.Mui-checked': { color: '#d84315' } }} />}
                          label="Train"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="Bus"
                          control={<Radio sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                          label="Bus"
                          disabled={isReadOnly}
                        />
                      </RadioGroup>
                      {errors.onwardTravelType && (
                        <Typography variant="caption" color="error">
                          {errors.onwardTravelType}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Onward Flight / Train No. / Bus No. *"
                      value={formData.onwardFlightTrainNo}
                      onChange={(e) => handleChange('onwardFlightTrainNo', e.target.value)}
                      InputProps={{ readOnly: isReadOnly }}
                      error={!!errors.onwardFlightTrainNo}
                      helperText={errors.onwardFlightTrainNo}
                      sx={{ bgcolor: 'white', borderRadius: 1 }}
                    />
                  </Grid>
                </Grid>

                {/* Seat Preference (Onward) */}
                {seatOptions.length > 0 && (
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Seat Preference from Origin</FormLabel>
                        <RadioGroup
                          row
                          value={formData.seatPreference}
                          onChange={(e) => handleChange('seatPreference', e.target.value)}
                        >
                          {seatOptions.map((opt) => (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={<Radio sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />}
                              label={opt.label}
                              disabled={isReadOnly}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}

                <Divider sx={{ my: 3, borderColor: '#a5d6a7' }} />
                  <Typography variant="subtitle1" fontWeight="600" color="#1b5e20" gutterBottom>
                  Travel & Accommodation Details Return 
                </Typography>
                {/* Destination */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!errors.destinationCountry}>
                      <InputLabel>Destination Country *</InputLabel>
                      <Select
                        value={formData.destinationCountry}
                        onChange={(e) => handleCountryChange(e.target.value, 'destination')}
                        disabled={isReadOnly}
                        label="Destination Country *"
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                      >
                        {countries.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.destinationCountry && (
                        <Typography variant="caption" color="error">
                          {errors.destinationCountry}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!errors.destinationState}>
                      <InputLabel>Destination State *</InputLabel>
                      <Select
                        value={formData.destinationState}
                        onChange={(e) => handleStateChange(e.target.value, 'destination')}
                        disabled={isReadOnly || !formData.destinationCountry}
                        label="Destination State *"
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                      >
                        {destStates.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.destinationState && (
                        <Typography variant="caption" color="error">
                          {errors.destinationState}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth size="small" error={!!errors.destination}>
                      <InputLabel>Destination City *</InputLabel>
                      <Select
                        value={formData.destination}
                        onChange={(e) => handleChange('destination', e.target.value)}
                        disabled={isReadOnly || !formData.destinationState}
                        label="Destination City *"
                        sx={{ bgcolor: 'white', borderRadius: 1 }}
                      >
                        {destCities.map((c) => (
                          <MenuItem key={c.id} value={c.id}>
                            {c.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.destination && (
                        <Typography variant="caption" color="error">
                          {errors.destination}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Return Travel */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={4}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="Return Travel Date *"
                        value={formData.returnTravelDate}
                        onChange={(date) => handleChange('returnTravelDate', date)}
                        disabled={isReadOnly}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: 'small',
                            error: !!errors.returnTravelDate,
                            helperText: errors.returnTravelDate,
                            sx: { bgcolor: 'white', borderRadius: 1 },
                          },
                        }}
                        format="DD/MM/YYYY"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <FormControl component="fieldset" error={!!errors.returnTravelType}>
                      <FormLabel component="legend">Return Travel Type *</FormLabel>
                      <RadioGroup
                        row
                        value={formData.returnTravelType}
                        onChange={(e) => handleTravelTypeSelect(e.target.value, 'return')}
                      >
                        <FormControlLabel
                          value="Flight"
                          control={<Radio sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />}
                          label="Flight"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="Train"
                          control={<Radio sx={{ color: '#d84315', '&.Mui-checked': { color: '#d84315' } }} />}
                          label="Train"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="Bus"
                          control={<Radio sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                          label="Bus"
                          disabled={isReadOnly}
                        />
                      </RadioGroup>
                      {errors.returnTravelType && (
                        <Typography variant="caption" color="error">
                          {errors.returnTravelType}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Return Flight / Train No. *"
                      value={formData.returnFlightTrainNo}
                      onChange={(e) => handleChange('returnFlightTrainNo', e.target.value)}
                      InputProps={{ readOnly: isReadOnly }}
                      error={!!errors.returnFlightTrainNo}
                      helperText={errors.returnFlightTrainNo}
                      sx={{ bgcolor: 'white', borderRadius: 1 }}
                    />
                  </Grid>
                </Grid>

                {/* Seat Preference (Return) */}
                {returnSeatOptions.length > 0 && (
                  <Grid container spacing={2} mb={2}>
                    <Grid item xs={12} md={12}>
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Seat Preference from Return</FormLabel>
                        <RadioGroup
                          row
                          value={formData.returnSeat}
                          onChange={(e) => handleChange('returnSeat', e.target.value)}
                        >
                          {returnSeatOptions.map((opt) => (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={<Radio sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />}
                              label={opt.label}
                              disabled={isReadOnly}
                            />
                          ))}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                  </Grid>
                )}

                <Divider sx={{ my: 3, borderColor: '#a5d6a7' }} />

                {/* Meal Preference */}
                <Grid container spacing={2} mb={2}>
                  <Grid item xs={12} md={12}>
                    <FormControl component="fieldset">
                      <FormLabel component="legend">Meal Preference</FormLabel>
                      <RadioGroup
                        row
                        value={formData.mealPreference}
                        onChange={(e) => handleChange('mealPreference', e.target.value)}
                      >
                        <FormControlLabel
                          value="Veg"
                          control={<Radio sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                          label="Veg"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="NonVeg"
                          control={<Radio sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />}
                          label="Non-Veg"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="diabetes Food"
                          control={<Radio sx={{ color: '#f57c00', '&.Mui-checked': { color: '#f57c00' } }} />}
                          label="Diabetes Food"
                          disabled={isReadOnly}
                        />
                        <FormControlLabel
                          value="Jain Food"
                          control={<Radio sx={{ color: '#6a1b9a', '&.Mui-checked': { color: '#6a1b9a' } }} />}
                          label="Jain Food"
                          disabled={isReadOnly}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </>
          )}

          {/* ─── Accommodation & Remarks ──────────────────────── */}
          <Box
            sx={{
              bgcolor: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
              p: 3,
              borderRadius: 2,
              mb: 4,
              borderLeft: '4px solid #6a1b9a',
            }}
          >
            <Typography variant="subtitle1" fontWeight="600" color="#4a148c" gutterBottom>
              <HotelIcon sx={{ mr: 1, verticalAlign: 'middle', color: '#6a1b9a' }} />
              Accommodation & Remarks
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Accommodation Required</FormLabel>
                  <RadioGroup
                    row
                    value={formData.accommodationRequired}
                    onChange={(e) => handleChange('accommodationRequired', e.target.value)}
                  >
                    <FormControlLabel
                      value="Yes"
                      control={<Radio sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />}
                      label="Yes"
                      disabled={isReadOnly}
                    />
                    <FormControlLabel
                      value="No"
                      control={<Radio sx={{ color: '#d32f2f', '&.Mui-checked': { color: '#d32f2f' } }} />}
                      label="No"
                      disabled={isReadOnly}
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  size="small"
                  label="Remark *"
                  value={formData.remarks}
                  onChange={(e) => handleChange('remarks', e.target.value)}
                  InputProps={{ readOnly: isReadOnly }}
                  error={!!errors.remarks}
                  helperText={errors.remarks}
                  multiline
                  rows={3}
                  sx={{ bgcolor: 'white', borderRadius: 1 }}
                />
              </Grid>
            </Grid>
          </Box>

          {/* ─── Action Buttons ────────────────────────────────── */}
          <Box mt={3} display="flex" gap={2} flexWrap="wrap" justifyContent="flex-end">
            {canSave && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSave}
                  disabled={saving}
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : buttonText === 'Edit' ? <EditIcon /> : <SaveIcon />}
                  sx={{
                    px: 4,
                    py: 1,
                    fontWeight: 700,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
                    },
                  }}
                >
                  {saving ? 'Saving...' : buttonText}
                </Button>
                {isEditMode && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleCancel}
                    startIcon={<CancelIcon />}
                    sx={{
                      px: 4,
                      py: 1,
                      fontWeight: 700,
                      borderRadius: 2,
                      borderColor: '#d32f2f',
                      color: '#d32f2f',
                      '&:hover': {
                        borderColor: '#b71c1c',
                        color: '#b71c1c',
                        backgroundColor: '#ffebee',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </>
            )}
            {showSendDO && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSendToDO}
                startIcon={<SendIcon />}
                sx={{
                  px: 4,
                  py: 1,
                  fontWeight: 700,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #1e88e5 0%, #0d47a1 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d47a1 0%, #0a3c7a 100%)',
                  },
                }}
              >
                Send to DO
              </Button>
            )}
          </Box>
        </Paper>
      </Container>

      {/* ─── Password Dialog ──────────────────────────────────── */}
      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Password</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Please enter your password to confirm sending this information to DO.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            error={!!passwordError}
            helperText={passwordError}
            disabled={passwordVerifying}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordConfirm();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordCancel} disabled={passwordVerifying}>
            Cancel
          </Button>
          <Button
            onClick={handlePasswordConfirm}
            variant="contained"
            color="primary"
            disabled={passwordVerifying}
            startIcon={passwordVerifying && <CircularProgress size={20} />}
          >
            {passwordVerifying ? 'Verifying...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AssessorLogisticDetails;